import type { ApolloError } from "@apollo/client";
import type { Row } from "@tanstack/react-table";
import { useEffect, useRef } from "react";

import { BaseTable } from "@/components/shared/data-table";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { SimpleInvoiceViewer } from "@/components/shared/file-viewers/simple-invoice-viewer";
import { AutoHeight } from "@/components/shared/page-layout/auto-height";
import { ServerSideDataTablePagination } from "@/components/shared/tables/server-side-table-pagination";
import type {
  InvoiceType,
  InvoiceTypeString,
} from "@/lib/constants/invoice-type";
import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";
import type { InvoiceFilterFields } from "@/types/utils";

import { PrefetchPdfOnHover } from "@/components/shared/pdf-tools/components/prefetch-pdf-on-hover";
import { TableBodyRow } from "@/components/ui/virtualized-table";
import type { VirtualItem } from "@tanstack/react-virtual";
import { DocumentSortingFilters } from "./document-sorting-filters";
import { TableActionBar } from "./table-action-bar";
import { useDocumentSotringTableInstance } from "./table-instance-provider";

type Props = {
  loading: boolean;
  onFilter: (filters: any) => void;
  config: any;
  filtersConfig?: {
    showStatusFilter?: boolean;
    showAccountFilter?: boolean;
    showSupplierFilter?: boolean;
    showExportsFilter?: boolean;
    showUploadButton?: boolean;
    showOcrReviewedFilter?: boolean;
    showExportAllButton?: boolean;
  };
  onReview: (invoiceIds: string[]) => void;
  onDelete: (invoiceIds: string[]) => void;
  onTypeChange: (invoiceIds: string[], type: InvoiceType) => void;
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filters: InvoiceFilterFields;
  setFilters: (filters: InvoiceFilterFields) => void;
  globalSearch: string;
  setGlobalSearch: (globalSearch: string) => void;
  error: ApolloError | undefined;
  invoiceType?: InvoiceTypeString;
};

export const DocumentSortingDataTable = ({
  loading,
  config,
  onFilter,
  filtersConfig = {},
  onReview,
  onDelete,
  onTypeChange,
  totalItems,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  filters,
  setFilters,
  globalSearch,
  setGlobalSearch,
  error,
  invoiceType,
}: Props) => {
  const tableRef = useRef(null);

  const {
    table,
    clickableColumns,
    columns,
    data,
    columnVisibility,
    stickyColumns,
  } = useDocumentSotringTableInstance();

  const { fetchPdf } = useInvoicePdfCache();

  const focusedRowId = table.getFocusedRowId();

  const rows = table.getRowModel()?.rows;

  useEffect(() => {
    focusedRowId && fetchPdf(focusedRowId);
  }, [focusedRowId, fetchPdf]);

  const handleRowClick = (
    event: React.MouseEvent,
    row: Row<any>,
    index: number,
  ) => {
    table.setFocusedRow(row.id);
  };

  useEffect(() => {
    if (rows.length > 0) {
      table.setFocusedRow(rows[0].id);
    }
  }, [rows.length, table.setFocusedRow]);

  const handleReview = (invoiceIds: string[]) => {
    onReview(invoiceIds);
  };

  const handleDelete = (invoiceIds: string[]) => {
    onDelete(invoiceIds);
  };

  return (
    <AutoHeight className="h-full gap-4">
      {({ height }) => (
        <div style={{ height }} className="flex flex-row gap-4">
          <div className="w-4/6 transition-all duration-100 ease-in-out">
            <div className="flex flex-col gap-4 h-full">
              <DocumentSortingFilters
                data={config}
                onFilter={onFilter}
                filtersConfig={filtersConfig}
                filters={filters}
                setFilters={setFilters}
                globalSearch={globalSearch}
                setGlobalSearch={setGlobalSearch}
                invoiceType={invoiceType}
              />
              <BaseTable
                table={table}
                clickableColumns={clickableColumns}
                columnVisibility={columnVisibility}
                columns={columns}
                data={data}
                stickyColumns={stickyColumns}
                loading={loading}
                ref={tableRef}
                onRowClick={handleRowClick}
                error={error?.message}
                emptyStateComponent={<NoRecordsEmpty state="done" />}
              >
                {({
                  rows,
                  rowVirtualizer,
                  table,
                  clickableColumns,
                  onRowClick,
                }) =>
                  rowVirtualizer
                    .getVirtualItems()
                    .map((virtualRow: VirtualItem) => {
                      const row = rows[virtualRow.index];
                      if (!row) return null;

                      return (
                        <PrefetchPdfOnHover key={row.id} invoiceId={row.id}>
                          <TableBodyRow
                            row={row}
                            virtualRow={virtualRow}
                            rowVirtualizer={rowVirtualizer}
                            table={table}
                            clickableColumns={clickableColumns}
                            onRowClick={onRowClick}
                            stickyColumns={stickyColumns}
                          />
                        </PrefetchPdfOnHover>
                      );
                    })
                }
              </BaseTable>
              <ServerSideDataTablePagination
                table={table}
                totalItems={totalItems}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
              <TableActionBar
                table={table}
                onDelete={handleDelete}
                onReview={handleReview}
                onTypeChange={onTypeChange}
                invoiceType={invoiceType}
              />
            </div>
          </div>
          <div className="h-full !overflow-y-auto transition-all duration-100 ease-in-out w-2/6">
            <div className="h-full flex flex-row">
              <div className="h-full w-full rounded-md border overflow-hidden">
                <SimpleInvoiceViewer invoiceId={focusedRowId} />
              </div>
            </div>
          </div>
        </div>
      )}
    </AutoHeight>
  );
};
